import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { StyledContactSettings } from "./index.styled";
import { getContactSettings } from "./utils";
import SettingsSectionTitle from "../SettingsSectionTitle";
import editUserIcon from "../../../assets/icons/user-edit.webp";
import editIcon from "../../../assets/icons/edit.webp";
import { Check, X } from "lucide-react";
import { updateContactField, fetchCategories } from "../../../utils/apiService";
import { fetchStatusMeAction } from "../../../store/dashboardSlice";
import type { RootState, AppDispatch } from "../../../store";

const ContactSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { status } = useSelector((state: RootState) => state.dashboard);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [tempValue, setTempValue] = useState("");
  const [categories, setCategories] = useState<{ id: number; name: string }[]>(
    []
  );
  const [loadingCategories, setLoadingCategories] = useState(false);

  React.useEffect(() => {
    const loadContactSettings = () => {
      const contactSettings = getContactSettings();
      setFullName(contactSettings.fullName);
      setPhone(contactSettings.phone);
      setCategory(contactSettings.category);
      setDescription(contactSettings.short_description);
    };
    loadContactSettings();
  }, [status]);

  React.useEffect(() => {
    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        const categoriesData = await fetchCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error("Error loading categories:", error);
      } finally {
        setLoadingCategories(false);
      }
    };
    loadCategories();
  }, []);

  const handleEdit = (fieldKey: string, currentValue: string) => {
    setEditingField(fieldKey);
    setTempValue(currentValue);
  };

  const hasChanges = (fieldKey: string) => {
    switch (fieldKey) {
      case "fullName":
        return tempValue !== fullName;
      case "phone":
        return tempValue !== phone;
      case "category":
        return tempValue !== category;
      case "description":
        return tempValue !== description;
      default:
        return false;
    }
  };

  const handleCancel = () => {
    setEditingField(null);
    setTempValue("");
  };

  const handleSave = async (fieldKey: string) => {
    if (!status?.id) {
      console.error("No status ID available");
      return;
    }

    try {
      switch (fieldKey) {
        case "fullName":
          // Update contact name via API
          await updateContactField(status.id, "contact_name", tempValue);
          setFullName(tempValue);
          break;
        case "phone":
          setPhone(tempValue);
          // Phone is read-only, no update needed
          break;
        case "category":
          // Update categories via API
          await updateContactField(status.id, "categories", [
            { Name: tempValue },
          ]);
          setCategory(tempValue);
          break;
        case "description":
          // Update short_description via API
          await updateContactField(status.id, "short_description", tempValue);
          setDescription(tempValue);
          break;
      }

      // Refresh status data from Redux after successful update
      dispatch(fetchStatusMeAction());

      setEditingField(null);
      setTempValue("");
    } catch (error) {
      console.error("Error saving contact field:", error);
      // You might want to show an error message to the user here
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, fieldKey: string) => {
    if (e.key === "Enter") {
      handleSave(fieldKey);
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const fieldData = [
    { value: fullName, label: "שם", key: "fullName", readOnly: false },
    { value: phone, label: "נייד", key: "phone", readOnly: true },
    { value: category, label: "קטגוריה", key: "category", readOnly: false },
    {
      value: description,
      label: "תיאור",
      key: "description",
      readOnly: false,
    },
  ];

  return (
    <>
      <SettingsSectionTitle title="פרטי איש קשר" icon={editUserIcon} />
      <StyledContactSettings>
        <div>
          {fieldData.map((field) => (
            <div
              key={field.key}
              className={`row ${field.readOnly ? "read-only" : ""} ${
                editingField === field.key ? "edit-mode" : ""
              }`}
            >
              <div className="label">{field.label}</div>
              {editingField === field.key ? (
                <div className="input-container">
                  {field.key === "category" ? (
                    <select
                      value={tempValue}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        setTempValue(e.target.value)
                      }
                      onKeyDown={(e) => handleKeyPress(e, field.key)}
                      autoFocus
                      className="input-field"
                      disabled={loadingCategories}
                    >
                      <option value="">בחר קטגוריה</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.name}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={tempValue}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setTempValue(e.target.value)
                      }
                      onKeyDown={(e) => handleKeyPress(e, field.key)}
                      autoFocus
                      className="input-field"
                    />
                  )}
                  <div className="button-container">
                    {hasChanges(field.key) && (
                      <button
                        className="save-button"
                        onClick={() => handleSave(field.key)}
                        title="שמור"
                      >
                        <Check size={16} />
                      </button>
                    )}
                    <button
                      className="cancel-button"
                      onClick={handleCancel}
                      title="בטל"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className={`display-value ${
                    field.readOnly ? "read-only" : ""
                  }`}
                  onClick={
                    field.readOnly
                      ? undefined
                      : () => handleEdit(field.key, field.value)
                  }
                >
                  {field.value}
                </div>
              )}
              {editingField !== field.key && !field.readOnly && (
                <button
                  className="edit-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(field.key, field.value);
                  }}
                >
                  <img src={editIcon} alt="edit" />
                </button>
              )}
            </div>
          ))}
        </div>
      </StyledContactSettings>
    </>
  );
};

export default ContactSettings;
